import clsx from 'clsx';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { BadgeIcon, type BadgeKind } from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { type AvatarSize, badgeOverAvatarSize, Tooltip } from '@gitbook/spine-react';

import { UserAvatar } from '../Users';
import { getChangeRequestStatusDescription } from './ChangeRequestStatusBadge';

export const CHANGE_REQUEST_STATUS_ICONS: Record<api.ChangeRequestStatus, SVGIconComponent> = {
    [api.ChangeRequestStatus.Draft]: Icon.ChangeRequestDraft,
    [api.ChangeRequestStatus.Open]: Icon.ChangeRequestOpen,
    [api.ChangeRequestStatus.Merged]: Icon.ChangeRequestMerged,
    [api.ChangeRequestStatus.Archived]: Icon.ChangeRequestArchive,
};

export const CHANGE_REQUEST_ICON_COLOR_CLASSNAMES: Record<api.ChangeRequestStatus, string> = {
    [api.ChangeRequestStatus.Draft]: 'text-secondary',
    [api.ChangeRequestStatus.Open]: 'text-success',
    [api.ChangeRequestStatus.Merged]: 'text-merge',
    [api.ChangeRequestStatus.Archived]: 'text-danger',
};

const CHANGE_REQUEST_BADGE_KINDS: Record<api.ChangeRequestStatus, BadgeKind> = {
    [api.ChangeRequestStatus.Draft]: 'secondary',
    [api.ChangeRequestStatus.Open]: 'success',
    [api.ChangeRequestStatus.Merged]: 'merge',
    [api.ChangeRequestStatus.Archived]: 'danger',
};

export function getChangeRequestIconBadge(status: api.ChangeRequestStatus): {
    icon: SVGIconComponent;
    kind: BadgeKind;
} {
    return {
        icon: CHANGE_REQUEST_STATUS_ICONS[status],
        kind: CHANGE_REQUEST_BADGE_KINDS[status],
    };
}

/**
 * Icon for a change request.
 */
export function ChangeRequestIcon(props: {
    changeRequest: api.ChangeRequest;
    size?: AvatarSize;
    className?: string;
}): React.ReactElement {
    const { changeRequest, size = 'md', className } = props;

    const tooltipContent = `${
        changeRequest.createdBy.displayName
    }'s change request is ${getChangeRequestStatusDescription(changeRequest.status).toLowerCase()}`;

    const iconSize = badgeOverAvatarSize[size];

    return (
        <Tooltip label={tooltipContent}>
            <div className={clsx('relative flex', className)}>
                <UserAvatar user={changeRequest.createdBy} size={size} tooltip={false} />
                <div
                    className="absolute flex rounded-full bg-base"
                    style={{
                        right: -iconSize / 2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: iconSize,
                        height: iconSize,
                    }}
                >
                    <BadgeIcon
                        size={badgeOverAvatarSize[size]}
                        {...getChangeRequestIconBadge(changeRequest.status)}
                    />
                </div>
            </div>
        </Tooltip>
    );
}
